import { useEffect, useState } from 'react';
import { LifeBuoy, Inbox, ExternalLink, Lock } from 'lucide-react';
import { api, timeAgo } from '../api.js';
import { UserChip } from '../components/UserChip.jsx';
import { useGuild } from './GuildLayout.jsx';

const CATEGORY_META = {
  help:    { label: 'Help',            emoji: '🆘' },
  feature: { label: 'Feature request', emoji: '✨' },
  bug:     { label: 'Bug report',      emoji: '🐛' },
  other:   { label: 'Other',           emoji: '💡' },
};

export default function SupportTickets() {
  const { guildId, channels } = useGuild();
  const [data, setData] = useState(null);
  const [cfg, setCfg] = useState(null);
  const [roles, setRoles] = useState([]);
  const [filter, setFilter] = useState('open');
  const [savingCfg, setSavingCfg] = useState(false);
  const [toast, setToast] = useState(null);

  useEffect(() => {
    reload();
    api.getConfig(guildId).then(setCfg);
    api.guildRoles(guildId).then(r => setRoles(r.roles));
  }, [guildId, filter]);

  function reload() {
    setData(null);
    api.listTickets(guildId, filter === 'all' ? null : filter).then(setData).catch(err => setData({ error: err.message }));
  }

  function fireToast(type, msg) { setToast({ type, msg }); setTimeout(() => setToast(null), 2500); }

  async function saveCfg(patch) {
    setSavingCfg(true);
    try {
      const updated = await api.putConfig(guildId, patch);
      setCfg(updated);
      fireToast('success', 'Saved');
    } catch (err) { fireToast('error', err.message); }
    finally { setSavingCfg(false); }
  }

  async function closeTicket(id) {
    const reason = prompt('Close reason (optional):');
    try { await api.closeTicket(guildId, id, reason || null); reload(); fireToast('success', 'Closed'); }
    catch (err) { fireToast('error', err.message); }
  }

  if (!data || !cfg) return <div className="card"><div className="skel skel-line med" /><div className="skel" style={{ height: 80, marginTop: 12 }} /></div>;
  if (data.error) return <div className="empty">Error: {data.error}</div>;

  const textChannels = channels.filter(c => c.type === 0 || c.type === 5);
  const isConfigured = cfg.support_parent_channel_id;

  return (
    <>
      <h1>Support Tickets</h1>
      <p className="subtitle">Members open tickets via a panel — private threads visible only to them and the support team.</p>

      <div className="card">
        <h3 style={{ marginTop: 0 }}>Configuration</h3>
        <div className="row">
          <div className="field">
            <label>Parent channel for tickets</label>
            <select value={cfg.support_parent_channel_id || ''} onChange={e => saveCfg({ support_parent_channel_id: e.target.value || null })} disabled={savingCfg}>
              <option value="">— pick a channel —</option>
              {textChannels.map(c => <option key={c.id} value={c.id}>#{c.name}</option>)}
            </select>
            <p className="dim" style={{ fontSize: 12, marginTop: 4 }}>Private threads will be created inside this channel.</p>
          </div>
          <div className="field">
            <label>Support team role</label>
            <select value={cfg.support_role_id || ''} onChange={e => saveCfg({ support_role_id: e.target.value || null })} disabled={savingCfg}>
              <option value="">— none —</option>
              {roles.map(r => <option key={r.id} value={r.id} disabled={!r.assignable}>{r.name}{!r.assignable ? ' (too high)' : ''}</option>)}
            </select>
            <p className="dim" style={{ fontSize: 12, marginTop: 4 }}>Role mentioned in every new ticket — they get auto-added to the thread.</p>
          </div>
        </div>
        <div className="field" style={{ marginTop: '.5rem' }}>
          <label>Close log channel (optional)</label>
          <select value={cfg.support_log_channel_id || ''} onChange={e => saveCfg({ support_log_channel_id: e.target.value || null })} disabled={savingCfg}>
            <option value="">— none —</option>
            {textChannels.map(c => <option key={c.id} value={c.id}>#{c.name}</option>)}
          </select>
          <p className="dim" style={{ fontSize: 12, marginTop: 4 }}>Where ticket-close summaries get posted. Skip if you only want the archived thread.</p>
        </div>
        {!isConfigured && <div className="dim" style={{ fontSize: 13, marginTop: 6 }}>Set the parent channel above, then run <span className="tag">/ticket setup</span> in your support panel channel to post the button panel.</div>}
        {isConfigured && <div className="dim" style={{ fontSize: 13, marginTop: 6 }}>Setup looks good. Run <span className="tag">/ticket setup</span> in whichever channel you want the panel posted (members click the buttons to open tickets).</div>}
      </div>

      <div className="stat-grid">
        <div className="stat warn">
          <div className="label">Open</div>
          <div className="value">{data.counts.open || 0}</div>
        </div>
        <div className="stat">
          <div className="label">Closed</div>
          <div className="value">{data.counts.closed || 0}</div>
        </div>
        <div className="stat info">
          <div className="label">Total</div>
          <div className="value">{data.counts.total || 0}</div>
        </div>
      </div>

      <div className="row" style={{ marginBottom: '1rem' }}>
        <div style={{ display: 'flex', gap: '.5rem' }}>
          {['open', 'closed', 'all'].map(f => (
            <button key={f} className={filter === f ? '' : 'ghost'} onClick={() => setFilter(f)}>{f[0].toUpperCase() + f.slice(1)}</button>
          ))}
        </div>
      </div>

      {!data.tickets.length ? (
        <div className="card empty"><div className="ico"><Inbox size={32} strokeWidth={1.5} /></div><p>No {filter === 'all' ? '' : filter} tickets.</p></div>
      ) : (
        <div className="card" style={{ padding: 0 }}>
          <table className="table">
            <thead><tr><th>#</th><th>Category</th><th>Subject</th><th>Creator</th><th>Status</th><th>When</th><th></th></tr></thead>
            <tbody>
              {data.tickets.map(t => {
                const cat = CATEGORY_META[t.category] || { label: t.category, emoji: '' };
                return (
                  <tr key={t.id}>
                    <td className="tag">#{t.id}</td>
                    <td>{cat.emoji} {cat.label}</td>
                    <td style={{ maxWidth: 320, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.subject || <span className="dim">(no subject)</span>}</td>
                    <td><UserChip guildId={guildId} userId={t.creator_id} size={20} /></td>
                    <td>
                      {t.status === 'open'
                        ? <span className="badge warn">open</span>
                        : <span className="badge"><Lock size={11} style={{ verticalAlign: 'middle', marginRight: 3 }} />closed</span>}
                    </td>
                    <td title={new Date(t.created_at * 1000).toLocaleString()}>{timeAgo(t.created_at)}</td>
                    <td>
                      <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
                        <a className="btn ghost" href={`https://discord.com/channels/${guildId}/${t.thread_id}`} target="_blank" rel="noreferrer" title="Open in Discord">
                          <ExternalLink size={13} strokeWidth={2} />
                        </a>
                        {t.status === 'open' && (
                          <button className="ghost danger-text" onClick={() => closeTicket(t.id)}>Close</button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {toast && <div className={`toast ${toast.type}`}>{toast.msg}</div>}
    </>
  );
}
